import { useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";
import { imageCache } from "../utils/imageCache";

const HeroImg = styled.img`
  object-fit: cover;
  user-select: none;
  pointer-events: none;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.35));
  border-radius: 9999px;
  width: 100%;
  height: 100%;
`;

export const HaloAnimation = () => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [cachedUrls, setCachedUrls] = useState<string[]>([]);

  // 圖片路徑陣列
  const animationImages = useMemo(
    () =>
      Array.from(
        { length: 179 },
        (_, i) =>
          `/animation/halo/halo_effect_${String(i).padStart(5, "0")}.png`
      ),
    []
  );

  // 預載並快取所有圖片到記憶體
  useEffect(() => {
    let isMounted = true;

    imageCache
      .loadBatch(animationImages)
      .then((objectUrls) => {
        if (isMounted) {
          setCachedUrls(objectUrls);
          setIsLoaded(true);
        }
      })
      .catch(() => {
        if (isMounted) {
          setHasError(true);
        }
      });

    return () => {
      isMounted = false;
      // 組件卸載時清理快取（可選，根據需求決定）
      // imageCache.clear();
    };
  }, [animationImages]);

  // 動畫播放邏輯（使用 requestAnimationFrame 提升性能）
  useEffect(() => {
    if (!isLoaded || hasError) return;

    let animationFrameId: number;
    let lastTime = performance.now();
    const frameInterval = 42; // 42ms per frame (~24 FPS)

    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;

      if (deltaTime >= frameInterval) {
        setCurrentFrame((prev) => (prev + 1) % cachedUrls.length); // 循環播放
        lastTime = currentTime - (deltaTime % frameInterval);
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isLoaded, hasError, cachedUrls.length]);

  // 載入中或錯誤狀態
  if (hasError || !isLoaded) {
    return (
      <HeroImg
        src="/animation/halo/halo_effect_00000.png"
        alt="Analyzing Animation"
      />
    );
  }

  return (
    <HeroImg
      src={cachedUrls[currentFrame]}
      alt={`Analyzing Animation Frame ${currentFrame + 1}`}
    />
  );
};
